import type { AnalyticsEventData, AnalyticsEventName } from '@tilly/shared';

type AnalyticsRuntime = typeof import('./analytics');
let runtimePromise: Promise<AnalyticsRuntime> | null = null;

function loadRuntime(): Promise<AnalyticsRuntime> {
  runtimePromise ??= import('./analytics');
  return runtimePromise;
}

function hasConsent(): boolean {
  return typeof window !== 'undefined' && localStorage.getItem('tilly_consent') === 'granted';
}

export function initAnalytics(): void {
  if (!hasConsent()) return;
  void loadRuntime().then((runtime) => runtime.initAnalytics());
}

export function initializeAnalyticsWhenIdle(): void {
  if (!hasConsent()) return;
  const initialize = () => initAnalytics();
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(initialize, { timeout: 2000 });
  } else {
    globalThis.setTimeout(initialize, 1000);
  }
}

export function trackExperimentView(experimentId: string): void {
  if (!hasConsent()) return;
  void loadRuntime().then((runtime) => runtime.trackExperimentView(experimentId));
}

export function trackSeoPageView(seoPageId: string): void {
  if (!hasConsent()) return;
  void loadRuntime().then((runtime) => runtime.trackSeoPageView(seoPageId));
}

export function trackOrganicEvent(
  eventName: AnalyticsEventName,
  params?: Record<string, unknown>
): void {
  if (!hasConsent()) return;
  void loadRuntime().then((runtime) => runtime.trackOrganicEvent(eventName, params));
}

export function trackAutomaticEvent(
  eventName: AnalyticsEventData['name'],
  params?: Record<string, unknown>
): void {
  if (!hasConsent()) return;
  void loadRuntime().then(async (runtime) => {
    runtime.initAnalytics();
    const { trackInternalEvent } = await import('./internalAnalytics');
    trackInternalEvent(eventName, params);
  });
}

export function setAutomaticExperimentContext(experimentId: string, variantId: string): void {
  if (!hasConsent()) return;
  void loadRuntime().then(async (runtime) => {
    runtime.initAnalytics();
    const { setInternalAnalyticsExperimentContext } = await import('./internalAnalytics');
    setInternalAnalyticsExperimentContext(experimentId, variantId);
  });
}

export function reportConversion(url?: string): boolean {
  const navigate = () => {
    if (url) window.location.href = url;
  };

  if (!hasConsent()) {
    navigate();
    return false;
  }

  const fallback = window.setTimeout(navigate, 1200);
  void loadRuntime()
    .then((runtime) => runtime.reportConversion(url))
    .catch(navigate)
    .finally(() => window.clearTimeout(fallback));
  return false;
}
